import React, { useState, useEffect } from 'react';
import Sidebar from '../../components/AdminDashboard/Sidebar';
import TopBar from '../../components/AdminDashboard/TopBar';
import MetaData from '../../layouts/MetaData';
import Loader from '../../layouts/Loader';
import { useAlert } from 'react-alert';
import { Link, useHistory } from 'react-router-dom';
import { Table } from 'react-bootstrap';
import axios from 'axios';
import moment from 'moment';
import { Prod01 } from '../../constants/ActionType';
import { useSortableTable, SortableTh, nameFromString, serverSortParams } from '../../components/SortableTable';
import { formatDate } from '../../utils/dateFormat';

const MONTHLY_SORT_COLUMNS = {
    name: { type: 'name', value: (r) => nameFromString(r?.name) },
    emrId: { type: 'string', value: (r) => r?.emrId },
    practice: { type: 'string', value: (r) => r?.practice },
    provider: { type: 'name', value: (r) => nameFromString(r?.clinician) },
    readings: { type: 'number', value: (r) => r?.readings },
    daysWithReadings: { type: 'number', value: (r) => r?.daysWithReadings },
    notes: { type: 'number', value: (r) => r?.notes },
};

// keys the ZIP endpoint can order by (backend monthlyReportRows whitelist)
const ZIP_SORT_KEYS = ['name', 'emrId', 'practice', 'provider', 'readings', 'notes'];

const previousMonth = () => moment().subtract(1, 'month').format('YYYY-MM');

const saveBlob = (blob, filename) => {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
};

const filenameFrom = (res, fallback) => {
    const cd = res.headers && res.headers['content-disposition'];
    const m = cd && cd.match(/filename="?([^"]+)"?/);
    return m ? m[1] : fallback;
};

const MonthlyReport = () => {
    const alert = useAlert();
    const history = useHistory();

    const [month, setMonth] = useState(previousMonth());
    const [doctorId, setDoctorId] = useState('');
    const [practice, setPractice] = useState('');
    const [withNotes, setWithNotes] = useState(true);
    const [includeEmpty, setIncludeEmpty] = useState(false);
    const [onlyWithReadings, setOnlyWithReadings] = useState(true);

    const [doctors, setDoctors] = useState([]);
    const [practices, setPractices] = useState([]);
    const [report, setReport] = useState(null);
    const [loading, setLoading] = useState(false);
    const [downloading, setDownloading] = useState('');

    useEffect(() => {
        axios.get(`${Prod01}/doctor/list/500/1`).then(({ data }) => setDoctors(Array.isArray(data) ? data : [])).catch(() => {});
        axios.get(`${Prod01}/general/report/monthly/practices`).then(({ data }) => setPractices(data?.practices || [])).catch(() => {});
    }, []);

    const filterBody = () => ({
        month,
        ...(doctorId && { doctorId }),
        ...(practice && { practice }),
    });

    const preview = async () => {
        if (!month) { alert.error('Please select a month'); return; }
        setLoading(true);
        try {
            const { data } = await axios.post(`${Prod01}/general/report/monthly/list`, filterBody());
            setReport(data);
        } catch (error) {
            alert.error(error?.response?.data?.message || error.message);
        }
        setLoading(false);
    };

    const downloadZip = async () => {
        if (!month) { alert.error('Please select a month'); return; }
        setDownloading('zip');
        try {
            const res = await axios.post(`${Prod01}/general/report/monthly/zip`, { ...filterBody(), withNotes, includeEmpty, ...serverSortParams(tableSort, ZIP_SORT_KEYS) }, { responseType: 'blob' });
            saveBlob(res.data, filenameFrom(res, `RPM_Monthly_${month}.zip`));
            alert.success('Monthly report package downloaded');
        } catch (error) {
            alert.error(error?.response?.data?.message || error.message);
        }
        setDownloading('');
    };

    const downloadPdf = async (row) => {
        setDownloading(row.patientId);
        try {
            const res = await axios.get(`${Prod01}/general/report/monthly/pdf/${row.patientId}`, { params: { month, withNotes }, responseType: 'blob' });
            saveBlob(res.data, filenameFrom(res, `${row.name}_${month}.pdf`));
        } catch (error) {
            alert.error(error?.response?.data?.message || error.message);
        }
        setDownloading('');
    };

    const filteredRows = report ? report.patients.filter(p => !onlyWithReadings || p.readings > 0) : [];
    const tableSort = useSortableTable(filteredRows, { id: 'admin.monthlyReport', columns: MONTHLY_SORT_COLUMNS, defaultSort: { key: 'name', dir: 'asc' } });
    const rows = tableSort.rows;

    return (
        <>
            <MetaData title="Monthly RPM Reports" />
            <Sidebar />

            <section className="home-section">
                <TopBar />

                <div className="shadow-lg p-3 mb-5 mr-4 ml-4 rounded-card" style={{ backgroundColor: '#FAFAFA' }}>
                    <div className="home-content">
                        <div className="row-display">
                            <h5 className="title">
                                <i className="bx bxs-file-pdf"></i> <strong>&nbsp;Monthly RPM Reports</strong>
                            </h5>
                            <div>
                                <button className="btn btn-secondary btn-sm" onClick={() => history.goBack()}>
                                    <i className="bx bx-arrow-back"></i>
                                </button>
                                &nbsp;
                                <Link className="link" to="/report/telemetary">Telemetry Data Report</Link>
                                &nbsp;
                                <Link to="/adminDashboard" className="btn btn-secondary btn-sm">
                                    <i className="bx bx-home"></i>
                                </Link>
                            </div>
                        </div>
                        <br />

                        <span className="notes-header">
                            <b>Note: </b> Generates one PDF per patient for the whole calendar month (1st to last day, Eastern Time) with every device reading
                            and the nurse / clinician notes attached to it. "Download all" delivers a ZIP with one folder per practice (or per clinician when no practice is set).
                        </span>

                        <div className="row-display mt-4">
                            <div className="col-md-2">
                                <b>Month <span style={{ color: 'red' }}> *</span></b>
                                <input type="month" value={month} max={moment().format('YYYY-MM')} onChange={e => { setMonth(e.target.value); setReport(null); }}
                                    className="form-control" />
                            </div>

                            <div className="col-md-3">
                                <b>Practice / Organization</b>
                                <select className="form-control" value={practice} onChange={e => { setPractice(e.target.value); setReport(null); }}>
                                    <option value="">All practices</option>
                                    {practices.map(p => <option key={p} value={p}>{p}</option>)}
                                </select>
                            </div>

                            <div className="col-md-3">
                                <b>Provider</b>
                                <select className="form-control" value={doctorId} onChange={e => { setDoctorId(e.target.value); setReport(null); }}>
                                    <option value="">All providers</option>
                                    {doctors.map(d => <option key={d._id} value={d._id}>{d.lastname}, {d.firstname}{d.practice ? ` (${d.practice})` : ''}</option>)}
                                </select>
                            </div>

                            <div className="col-md-2 mt-4">
                                <div className="form-check">
                                    <input className="form-check-input" type="checkbox" id="withNotes" checked={withNotes} onChange={e => setWithNotes(e.target.checked)} />
                                    <label className="form-check-label" htmlFor="withNotes">Include notes</label>
                                </div>
                                <div className="form-check">
                                    <input className="form-check-input" type="checkbox" id="includeEmpty" checked={includeEmpty} onChange={e => setIncludeEmpty(e.target.checked)} />
                                    <label className="form-check-label" htmlFor="includeEmpty">Include patients with no readings</label>
                                </div>
                            </div>

                            <div className="col-md-2 mt-4">
                                <button className="edit-pill" onClick={preview} disabled={loading}>Preview</button>
                                &nbsp;
                                <button className="edit-pill" onClick={downloadZip} disabled={downloading === 'zip'}>
                                    <i className="bx bxs-download"></i> {downloading === 'zip' ? 'Preparing...' : 'Download all (ZIP)'}
                                </button>
                            </div>
                        </div>

                        <hr />

                        {loading ? <Loader /> : report && (
                            <>
                                <div className="row-display">
                                    <p className="m-0">
                                        <b>{report.label}</b> ({formatDate(report.from)} - {formatDate(report.to)}) &nbsp;|&nbsp;
                                        <b>{report.withReadings}</b> patients with readings, <b>{report.withoutReadings}</b> without
                                        {report.deactivationsByReason && <>
                                            <br />
                                            <small data-testid="deactivations-by-reason">
                                                <b>{report.deactivationsByReason.total}</b> deactivated in {report.label}
                                                {report.deactivationsByReason.byReason?.length > 0 && ': '}
                                                {(report.deactivationsByReason.byReason || []).map((r) => `${r.label} ${r.count}`).join(', ')}
                                            </small>
                                        </>}
                                    </p>
                                    <div className="form-check">
                                        <input className="form-check-input" type="checkbox" id="onlyWithReadings" checked={onlyWithReadings} onChange={e => setOnlyWithReadings(e.target.checked)} />
                                        <label className="form-check-label" htmlFor="onlyWithReadings">Show only patients with readings</label>
                                    </div>
                                </div>

                                <div className="col-md-12 mt-3">
                                    <Table responsive className="table table-sm table-striped">
                                        <thead align="center">
                                            <tr>
                                                <SortableTh sortKey="name" sort={tableSort}>Patient</SortableTh>
                                                <SortableTh sortKey="emrId" sort={tableSort}>EHR ID</SortableTh>
                                                <SortableTh sortKey="practice" sort={tableSort}>Practice</SortableTh>
                                                <SortableTh sortKey="provider" sort={tableSort} className="text-nowrap">Provider</SortableTh>
                                                <SortableTh sortKey="readings" sort={tableSort}>Readings</SortableTh>
                                                <SortableTh sortKey="daysWithReadings" sort={tableSort}>Days with readings</SortableTh>
                                                <SortableTh sortKey="notes" sort={tableSort}>Notes</SortableTh>
                                                <th>PDF</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {rows.length === 0 ? (
                                                <tr><td colSpan="8" className="text-center">No patients match this filter for {report.label}.</td></tr>
                                            ) : rows.map(row => (
                                                <tr key={row.patientId} align="center">
                                                    <td className="text-left">
                                                        <Link className="link" to={{ pathname: '/patientprofile', state: { patientid: row.patientId } }}>{row.name}</Link>
                                                        {!row.active && <small className="text-danger"> (inactive)</small>}
                                                    </td>
                                                    <td>{row.emrId || '-'}</td>
                                                    <td>{row.practice || '-'}</td>
                                                    <td className="text-nowrap">{row.clinician}</td>
                                                    <td>{row.readings}</td>
                                                    <td>{row.daysWithReadings}</td>
                                                    <td>{row.notes}</td>
                                                    <td>
                                                        <button className="edit-pill" onClick={() => downloadPdf(row)} disabled={downloading === row.patientId}>
                                                            <i className="bx bxs-file-pdf" style={{ color: '#F40F02' }}></i> {downloading === row.patientId ? '...' : 'PDF'}
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </section>
        </>
    );
};

export default MonthlyReport;
